import React, { useState, useEffect } from 'react';
import { Button, Input, Typography, Table, Tag, Spin, Card, Collapse, Toast, Space, Tabs } from '@douyinfe/semi-ui';
import { IconSearch, IconCopy, IconDownload } from '@douyinfe/semi-icons';
import { API, timestamp2string, copy } from '../helpers';
import { stringToColor } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';
import { renderModelPrice, renderQuota } from '../helpers/render';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import { Tooltip, Modal } from '@douyinfe/semi-ui';
import Papa from 'papaparse';

const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

function renderTimestamp(timestamp) {
    return timestamp2string(timestamp);
}

function renderIsStream(bool) {
    if (bool) {
        return <Tag color="blue" size="large">流</Tag>;
    } else {
        return <Tag color="purple" size="large">非流</Tag>;
    }
}

function renderUseTime(type) {
    const time = parseInt(type);
    if (time < 101) {
        return <Tag color="green" size="large"> {time} 秒 </Tag>;
    } else if (time < 300) {
        return <Tag color="orange" size="large"> {time} 秒 </Tag>;
    } else {
        return <Tag color="red" size="large"> {time} 秒 </Tag>;
    }
}

const LogsTable = () => {
    const [apikey, setAPIKey] = useState('');
    const [activeTabKey, setActiveTabKey] = useState('');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(false);
    const [activeKeys, setActiveKeys] = useState([]);
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
    const [baseUrl, setBaseUrl] = useState('');
    const baseUrls = JSON.parse(process.env.REACT_APP_BASE_URL);  // 解析环境变量

    useEffect(() => {
        // 默认设置第一个地址为baseUrl
        const firstKey = Object.keys(baseUrls)[0];
        setActiveTabKey(firstKey);
        setBaseUrl(baseUrls[firstKey]);
    }, []);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key]);
    };

    const resetData = (key) => {
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                totalGranted: 0,
                totalUsed: 0,
                totalAvailable: 0,
                unlimitedQuota: false,
                expiresAt: 0,
                tokenName: '',
                logs: [],
                tokenValid: false,
            }
        }));
    };

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        // 检查令牌格式
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }
        setLoading(true);
        let newTabData = { ...tabData[activeTabKey], totalGranted: 0, totalUsed: 0, totalAvailable: 0, unlimitedQuota: false, expiresAt: 0, tokenName: '', logs: [], tokenValid: false };

        try {

            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                const usageRes = await API.get(`${baseUrl}/api/usage/token/`, {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const usageData = usageRes.data;
                if (usageData.code) {
                    const d = usageData.data;
                    newTabData.unlimitedQuota = d.unlimited_quota;
                    newTabData.totalGranted = d.total_granted;
                    newTabData.totalUsed = d.total_used;
                    newTabData.totalAvailable = d.total_available;
                    newTabData.expiresAt = d.expires_at;
                    newTabData.tokenName = d.name;
                    newTabData.tokenValid = true;
                } else {
                    Toast.error(usageData.message || '查询令牌信息失败');
                }
            }
        } catch (e) {
            console.log(e)
            Toast.error("查询令牌信息失败，请检查令牌是否正确");
            resetData(activeTabKey); // 如果发生错误，重置所有数据为默认值
            setLoading(false);
        }
        try {
            if (process.env.REACT_APP_SHOW_DETAIL === "true") {
                const logRes = await API.get(`${baseUrl}/api/log/token`, {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const { success, message, data: logData } = logRes.data;
                if (success) {
                    newTabData.logs = logData.reverse();
                    let quota = 0;
                    for (let i = 0; i < logData.length; i++) {
                        quota += logData[i].quota;
                    }
                    setActiveKeys(['1', '2']); // 自动展开两个折叠面板
                } else {
                    Toast.error('查询调用详情失败，请输入正确的令牌');
                }
            }
        } catch (e) {
            Toast.error("查询失败，请输入正确的令牌");
            resetData(activeTabKey); // 如果发生错误，重置所有数据为默认值
            setLoading(false);
        }
        setTabData((prevData) => ({
            ...prevData,
            [activeTabKey]: newTabData,
        }));
        setLoading(false);

    };

    const copyText = async (text) => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                Toast.success('已复制：' + text);
                return;
            }
            
            // Fallback for Safari and older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                textArea.remove();
                Toast.success('已复制：' + text);
            } catch (err) {
                textArea.remove();
                Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
            }
        } catch (err) {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: renderTimestamp,
            sorter: (a, b) => a.created_at - b.created_at,
            defaultSortOrder: 'descend',
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color="grey"
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color={stringToColor(text)}
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    <div>
                        <Space>
                            {renderUseTime(text)}
                            {renderIsStream(record.is_stream)}
                        </Space>
                    </div>
                );
            },
            sorter: (a, b) => a.use_time - b.use_time,
        },
        {
            title: '提示',
            dataIndex: 'prompt_tokens',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    record.type === 0 || record.type === 2 ? <div>{<span> {text} </span>}</div> : <></>
                );
            },
            sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
        },
        {
            title: '补全',
            dataIndex: 'completion_tokens',
            render: (text, record, index) => {
                return parseInt(text) > 0 && (record.type === 0 || record.type === 2) ? (
                    <div>{<span> {text} </span>}</div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => a.completion_tokens - b.completion_tokens,
        },
        {
            title: '花费',
            dataIndex: 'quota',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? <div>{renderQuota(text, 6)}</div> : <></>;
            },
            sorter: (a, b) => a.quota - b.quota,
        },
        {
            title: '详情',
            dataIndex: 'content',
            render: (text, record, index) => {
                let other = null;
                try {
                    if (record.other === '') {
                        record.other = '{}';
                    }
                    other = JSON.parse(record.other);
                } catch (e) {
                    return (
                        <Tooltip content="该版本不支持显示计算详情">
                            <Paragraph
                                ellipsis={{
                                    rows: 2,
                                }}
                            >
                                {text}
                            </Paragraph>
                        </Tooltip>
                    );
                }
                if (other == null) {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {text}
                        </Paragraph>
                    );
                }
                let content = renderModelPrice(
                    record.prompt_tokens,
                    record.completion_tokens,
                    other.model_ratio,
                    other.model_price,
                    other.completion_ratio,
                    other.group_ratio,
                );
                return (
                    <Tooltip content={content}>
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                            }}
                        >
                            {text}
                        </Paragraph>
                    </Tooltip>
                );
            },
        }
    ];

    const copyTokenInfo = (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || {};
        const { totalGranted, totalUsed, totalAvailable, unlimitedQuota, expiresAt, tokenName } = activeTabData;
        const info = `令牌名称: ${tokenName || '未知'}
令牌总额: ${unlimitedQuota ? '无限' : renderQuota(totalGranted, 3)}
剩余额度: ${unlimitedQuota ? '无限制' : renderQuota(totalAvailable, 3)}
已用额度: ${unlimitedQuota ? '不进行计算' : renderQuota(totalUsed, 3)}
有效期至: ${expiresAt === 0 ? '永不过期' : renderTimestamp(expiresAt)}`;
        copyText(info);
    };

    const exportCSV = (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || { logs: [] };
        const { logs } = activeTabData;
        const csvData = logs.map(log => ({
            '时间': renderTimestamp(log.created_at),
            '模型': log.model_name,
            '用时': log.use_time,
            '提示': log.prompt_tokens,
            '补全': log.completion_tokens,
            '花费': log.quota,
            '详情': log.content,
        }));
        const csvString = '\ufeff' + Papa.unparse(csvData);
        
        try {
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'data.csv';
            
            // For Safari compatibility
            if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
                link.target = '_blank';
                link.setAttribute('target', '_blank');
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (err) {
            Toast.error('导出失败，请稍后重试');
            console.error('Export failed:', err);
        }
    };

    const activeTabData = tabData[activeTabKey] || { logs: [], totalGranted: 0, totalUsed: 0, totalAvailable: 0, unlimitedQuota: false, expiresAt: 0, tokenName: '', tokenValid: false };

    const renderContent = () => (
        <section className="jm-key-page">

            <section className="jm-key-hero">

                <div className="jm-key-eyebrow">
                    <i />
                    TOKEN CONSOLE · JINGMU API
                </div>

                <div className="jm-key-hero-grid">

                    <div>

                        <h1>
                            令牌额度查询
                        </h1>

                        <p>
                            查询令牌余额、已用额度、有效期与调用明细。
                        </p>

                    </div>

                    <div className="jm-key-security">

                        <span className="jm-security-dot" />

                        <div>

                            <strong>
                                安全查询
                            </strong>

                            <small>
                                令牌仅保存在当前页面内存中，
                                不写入本地存储。
                            </small>

                        </div>

                    </div>

                </div>

            </section>


            <Card className="jm-query-card">

                <div className="jm-query-label">

                    <span>
                        API TOKEN
                    </span>

                    <small>
                        支持 sk- 开头的鲸木 API 令牌
                    </small>

                </div>

                <Input
                    className="jm-token-input"
                    size="large"
                    showClear
                    value={apikey}
                    onChange={(value) => setAPIKey(value)}
                    placeholder="请输入需要查询的令牌"
                    prefix={<IconSearch />}
                    suffix={
                        <Button
                            className="jm-query-button"
                            type='primary'
                            theme="solid"
                            onClick={fetchData}
                            loading={loading}
                            disabled={apikey === ''}
                        >
                            查询
                        </Button>
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            fetchData();
                        }
                    }}
                />

                <div className="jm-query-hint">

                    <span>
                        不要在截图、聊天或公开页面中暴露完整令牌
                    </span>

                    <i>
                        HTTPS
                    </i>

                </div>

            </Card>


            <Card className="jm-result-card">

                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(keys)}
                >

                    {process.env.REACT_APP_SHOW_BALANCE === "true" && (

                        <Panel
                            header={
                                <div className="jm-panel-heading">

                                    <span className="jm-panel-index">
                                        01
                                    </span>

                                    <span>
                                        令牌信息
                                    </span>

                                </div>
                            }
                            itemKey="1"
                            extra={
                                <Button
                                    icon={<IconCopy />}
                                    theme='borderless'
                                    type='primary'
                                    onClick={(e) => copyTokenInfo(e)}
                                    disabled={!activeTabData.tokenValid}
                                >
                                    复制令牌信息
                                </Button>
                            }
                        >

                            <Spin spinning={loading}>

                                <div className="jm-metric-grid">

                                    <article className="jm-metric jm-metric-wide">

                                        <span>
                                            令牌名称
                                        </span>

                                        <strong>
                                            {
                                                activeTabData.tokenValid
                                                ? activeTabData.tokenName || '未命名'
                                                : '—'
                                            }
                                        </strong>

                                    </article>


                                    <article className="jm-metric">

                                        <span>
                                            令牌总额
                                        </span>

                                        <strong>
                                            {
                                                activeTabData.unlimitedQuota
                                                ? '无限'
                                                : !activeTabData.tokenValid
                                                ? '—'
                                                : renderQuota(
                                                    activeTabData.totalGranted,
                                                    3
                                                )
                                            }
                                        </strong>

                                    </article>


                                    <article className="jm-metric jm-metric-primary">

                                        <span>
                                            剩余额度
                                        </span>

                                        <strong>
                                            {
                                                activeTabData.unlimitedQuota
                                                ? '无限制'
                                                : !activeTabData.tokenValid
                                                ? '—'
                                                : renderQuota(
                                                    activeTabData.totalAvailable,
                                                    3
                                                )
                                            }
                                        </strong>

                                    </article>


                                    <article className="jm-metric">

                                        <span>
                                            已用额度
                                        </span>

                                        <strong>
                                            {
                                                activeTabData.unlimitedQuota
                                                ? '不进行计算'
                                                : !activeTabData.tokenValid
                                                ? '—'
                                                : renderQuota(
                                                    activeTabData.totalUsed,
                                                    3
                                                )
                                            }
                                        </strong>

                                    </article>


                                    <article className="jm-metric">

                                        <span>
                                            有效期至
                                        </span>

                                        <strong>
                                            {
                                                !activeTabData.tokenValid
                                                ? '—'
                                                : activeTabData.expiresAt === 0
                                                ? '永不过期'
                                                : renderTimestamp(
                                                    activeTabData.expiresAt
                                                )
                                            }
                                        </strong>

                                    </article>

                                </div>

                            </Spin>

                        </Panel>

                    )}


                    {process.env.REACT_APP_SHOW_DETAIL === "true" && (

                        <Panel
                            header={
                                <div className="jm-panel-heading">

                                    <span className="jm-panel-index">
                                        02
                                    </span>

                                    <span>
                                        调用详情
                                    </span>

                                </div>
                            }
                            itemKey="2"
                            extra={
                                <div className="jm-log-actions">

                                    <Tag
                                        shape='circle'
                                        color='green'
                                    >
                                        计费基准 · $1 = 500,000 tokens
                                    </Tag>

                                    <Button
                                        icon={<IconDownload />}
                                        theme='borderless'
                                        type='primary'
                                        onClick={(e) => exportCSV(e)}
                                        disabled={
                                            !activeTabData.tokenValid ||
                                            activeTabData.logs.length === 0
                                        }
                                    >
                                        导出 CSV
                                    </Button>

                                </div>
                            }
                        >

                            <Spin spinning={loading}>

                                <div className="jm-table-shell">

                                    <Table
                                        columns={columns}
                                        dataSource={activeTabData.logs}
                                        pagination={{
                                            pageSize: pageSize,
                                            hideOnSinglePage: true,
                                            showSizeChanger: true,
                                            pageSizeOpts: [10, 20, 50, 100],
                                            onPageSizeChange: (pageSize) =>
                                                setPageSize(pageSize),
                                            showTotal: (total) =>
                                                `共 ${total} 条`,
                                            showQuickJumper: true,
                                            total: activeTabData.logs.length,
                                            style: {
                                                marginTop: 12
                                            },
                                        }}
                                    />

                                </div>

                            </Spin>

                        </Panel>

                    )}

                </Collapse>

            </Card>


            <footer className="jm-key-footer">

                <span>
                    JINGMU API · TOKEN CONSOLE
                </span>

                <span>
                    当前查询节点 · 鲸木AI
                </span>

            </footer>

        </section>
    );

    return (
        <>
            {Object.keys(baseUrls).length > 1 ? (
                <Tabs type="line" onChange={handleTabChange}>
                    {Object.entries(baseUrls).map(([key, url]) => (
                        <TabPane tab={key} itemKey={key} key={key}>
                            {renderContent()}
                        </TabPane>
                    ))}
                </Tabs>
            ) : (
                renderContent()
            )}
        </>
    );
};

export default LogsTable;
